import { OrderLineItemDto } from '../../../dtos';
import { formatDate, shouldHideSiteNameOnDispatchUi } from '../../../util';

export const getOrderLineItemTooltipData = (orderLineItem: OrderLineItemDto) => {
    const lineItemType = orderLineItem.lineItemType ?? orderLineItem.lineItem?.type;
    const hideSiteName = shouldHideSiteNameOnDispatchUi(lineItemType);
    const formatIntervalTime = (orderLineItem: OrderLineItemDto) => {
        if (!orderLineItem?.dispatchingIntervalTime) {
            return '';
        }
        
        const intervalCount = orderLineItem?.intervalCount ?? 0;
        const equipmentType = orderLineItem?.equipmentTypeType ?? '';
        const intervalTimeMinutes = orderLineItem.dispatchingIntervalTime;
        
        return `${intervalCount} ${equipmentType} every ${intervalTimeMinutes} min.`;
    };

    let intervalTime = formatIntervalTime(orderLineItem);

    const getHaulToAddressForOrderLineItem = (orderLineItem: OrderLineItemDto) => {
        let address = '';

        const addressParts = [
            orderLineItem.haulToSiteName,
            orderLineItem.haulToRegion?.name,
            orderLineItem.haulToAddressLine1,
            orderLineItem.haulToAddressLine2,
        ];

        address += addressParts.filter((part) => part && part.trim() !== '').join('<br> ');

        if (orderLineItem.haulToCity && orderLineItem.haulToCity.trim() !== '') {
            address += `<br> ${orderLineItem.haulToCity}`;
            if (orderLineItem.haulToState && orderLineItem.haulToState.trim() !== '') {
                address += `,`;
            }
        }
        if (orderLineItem.haulToState && orderLineItem.haulToState.trim() !== '') {
            if (!orderLineItem.haulToCity || orderLineItem.haulToCity.trim() === '') {
                address += `<br>`;
            }
            address += ` ${orderLineItem.haulToState}`;
        }
        if (orderLineItem.haulToZipCode && orderLineItem.haulToZipCode.trim() !== '') {
            address += ` ${orderLineItem.haulToZipCode}`;
        }

        if (orderLineItem.haulToDirections && orderLineItem.haulToDirections.trim() !== '') {
            address += `<br> ${truncateStringWithDots(orderLineItem.haulToDirections, 100)}`;
        }

        if (address.startsWith('<br>')) {
            address = address.substring(4);
        }

        return address;
    };

    const getHaulFromAddressForOrderLineItem = (orderLineItem: OrderLineItemDto) => {
        let address = '';

        const addressParts = [
            orderLineItem.haulFromSiteName,
            orderLineItem.haulFromRegion?.name,
            orderLineItem.haulFromAddressLine1,
            orderLineItem.haulFromAddressLine2,
        ];

        address += addressParts.filter((part) => part && part.trim() !== '').join('<br> ');

        if (orderLineItem.haulFromCity && orderLineItem.haulFromCity.trim() !== '') {
            address += `<br> ${orderLineItem.haulFromCity}`;
            if (orderLineItem.haulFromState && orderLineItem.haulFromState.trim() !== '') {
                address += `,`;
            }
        }
        if (orderLineItem.haulFromState && orderLineItem.haulFromState.trim() !== '') {
            if (!orderLineItem.haulFromCity || orderLineItem.haulFromCity.trim() === '') {
                address += `<br>`;
            }
            address += ` ${orderLineItem.haulFromState}`;
        }
        if (orderLineItem.haulFromZipCode && orderLineItem.haulFromZipCode.trim() !== '') {
            address += ` ${orderLineItem.haulFromZipCode}`;
        }

        if (orderLineItem.haulFromDirections && orderLineItem.haulFromDirections.trim() !== '') {
            address += `<br> ${truncateStringWithDots(orderLineItem.haulFromDirections, 100)}`;
        }

        if (address.startsWith('<br>')) {
            address = address.substring(4);
        }

        return address;
    };

    const truncateStringWithDots = (value: string, maxLength: number) => {
        if (!value) {
            return value;
        }

        return value.length <= maxLength ? value : value.substring(0, maxLength).trimEnd() + "...";
    }

    const getMemos = (orderLineItem: OrderLineItemDto) => {
        let memoHtml = `<div class="memo-container">
                                <div class="memo-body">
                                    <div class="memo-row">
                                        <span class="memo-label">Dispatch/Billing Memo:</span>
                                        <span class="memo-value">${orderLineItem?.dispatchBillingMemo ?? ''}</span>
                                    </div>
                                    <div class="divider"></div>
                                    <div class="memo-row">
                                        <span class="memo-label">Order Memo:</span>
                                        <span class="memo-value">${orderLineItem?.driverMemo ?? ''}</span>
                                    </div>`;
        
        if (orderLineItem?.canceledOn && orderLineItem?.cancellationReason) {
            memoHtml += `<div class="divider"></div>
                                    <div class="memo-row">
                                        <span class="memo-label">Cancellation Reason:</span>
                                        <span class="memo-value">${orderLineItem?.cancellationReason ?? ''}</span>
                                    </div>`;
        }
        
        memoHtml += `</div>
                            </div>`;
        return memoHtml;
    };

    const getContactInfo = (orderLineItem: OrderLineItemDto) => {
        let contactInfo = ``;
        const contactFields: string[] = [];

        if (orderLineItem?.contactName && orderLineItem?.contactName.trim() !== '') {
            contactFields.push(`<span class="tooltip-label">Contact:</span><span class="tooltip-value">${orderLineItem?.contactName ?? ''}</span>`);
        }
        if (orderLineItem?.contactEmail) {
            contactFields.push(`<span class="tooltip-label">Email:</span><span class="tooltip-value"><a href="mailto:${orderLineItem?.contactEmail ?? ''}">${orderLineItem?.contactEmail ?? ''}</a></span>`);
        }
        if (orderLineItem?.contactCellPhone) {
            contactFields.push(`<span class="tooltip-label">Cell:</span><span class="tooltip-value"><a href="tel:${orderLineItem?.contactCellPhone ?? ''}">${orderLineItem?.contactCellPhone ?? ''}</a></span>`);
        }
        if (orderLineItem?.contactLandlinePhone) {
            contactFields.push(`<span class="tooltip-label">Landline:</span><span class="tooltip-value"><a href="tel:${orderLineItem?.contactLandlinePhone ?? ''}">${orderLineItem?.contactLandlinePhone ?? ''}</a></span>`);
        }
        if (orderLineItem?.contactFax) {
            contactFields.push(`<span class="tooltip-label">Fax:</span><span class="tooltip-value"><a href="tel:${orderLineItem?.contactFax ?? ''}">${orderLineItem?.contactFax ?? ''}</a></span>`);
        }
        if (contactFields.length > 0) {
            contactInfo += `<div class="divider"></div>`;
        }

        // Group fields into rows of two columns
        for (let i = 0; i < contactFields.length; i += 2) {
            const leftField = contactFields[i];
            const rightField = contactFields[i + 1] || '';
            contactInfo += `<div class="tooltip-row">
                                <div class="tooltip-column">${leftField}</div>
                                <div class="tooltip-column">${rightField}</div>
                            </div>`;
        }

        if (orderLineItem?.contactMemo) {
            contactInfo += `<div class="memo-row" style="max-height: 75px; overflow-y: auto;">
                                <span class="memo-label">Memo:</span>
                                <span class="memo-value">${orderLineItem?.contactMemo ?? ''}</span>
                            </div>`;
        }
        return contactInfo;
    };

    const getOrderedByInfo = (orderLineItem: OrderLineItemDto) => {
        let orderedByInfo = ``;
        const orderedByFields: string[] = [];

        if (orderLineItem?.orderedByName && orderLineItem?.orderedByName.trim() !== '') {
            orderedByFields.push(`<span class="tooltip-label">Ordered By:</span><span class="tooltip-value">${orderLineItem?.orderedByName ?? ''}</span>`);
        }
        if (orderLineItem?.orderedByEmail) {
            orderedByFields.push(`<span class="tooltip-label">Email:</span><span class="tooltip-value"><a href="mailto:${orderLineItem?.orderedByEmail ?? ''}">${orderLineItem?.orderedByEmail ?? ''}</a></span>`);
        }
        if (orderLineItem?.orderedByCellPhone) {
            orderedByFields.push(`<span class="tooltip-label">Cell:</span><span class="tooltip-value"><a href="tel:${orderLineItem?.orderedByCellPhone ?? ''}">${orderLineItem?.orderedByCellPhone ?? ''}</a></span>`);
        }
        if (orderLineItem?.orderedByLandlinePhone) {
            orderedByFields.push(`<span class="tooltip-label">Landline:</span><span class="tooltip-value"><a href="tel:${orderLineItem?.orderedByLandlinePhone ?? ''}">${orderLineItem?.orderedByLandlinePhone ?? ''}</a></span>`);
        }
        if (orderLineItem?.orderedByFax) {
            orderedByFields.push(`<span class="tooltip-label">Fax:</span><span class="tooltip-value"><a href="tel:${orderLineItem?.orderedByFax ?? ''}">${orderLineItem?.orderedByFax ?? ''}</a></span>`);
        }
        if (orderedByFields.length > 0) {
            orderedByInfo += `<div class="divider"></div>`;
        }

        // Group fields into rows of two columns
        for (let i = 0; i < orderedByFields.length; i += 2) {
            const leftField = orderedByFields[i];
            const rightField = orderedByFields[i + 1] || '';
            orderedByInfo += `<div class="tooltip-row">
                                <div class="tooltip-column">${leftField}</div>
                                <div class="tooltip-column">${rightField}</div>
                            </div>`;
        }

        if (orderLineItem?.orderedByMemo) {
            orderedByInfo += `<div class="memo-row" style="max-height: 75px; overflow-y: auto;">
                                <span class="memo-label">Memo:</span>
                                <span class="memo-value">${orderLineItem?.orderedByMemo ?? ''}</span>
                            </div>`;
        }
        return orderedByInfo;
    };

    const toolTipHtml = `<div class="html-container">
                            <div class="flex-box">
                                <div class="tooltip-container">
                                    <div class="tooltip-body">
                                        <div class="tooltip-row">
                                            <span class="tooltip-label">Customer:</span>
                                            <span class="tooltip-value">${orderLineItem?.customerName ?? ''}</span>
                                        </div>
                                        <div class="tooltip-row">
                                            <span class="tooltip-label">Customer Number:</span>
                                            <span class="tooltip-value">${orderLineItem?.customerNumber ?? ''}</span>
                                        </div>
                                        <div class="tooltip-row">
                                            <span class="tooltip-label">Orderer:</span>
                                            <span class="tooltip-value">${orderLineItem?.orderer ?? ''}</span>
                                        </div>
                                        <div class="tooltip-row">
                                            <span class="tooltip-label">Project Name:</span>
                                            <span class="tooltip-value">${orderLineItem?.projectName ?? ''}</span>
                                        </div>
                                        <div class="tooltip-row">
                                            <span class="tooltip-label">Job Name:</span>
                                            <span class="tooltip-value">${orderLineItem?.jobName ?? ''}</span>
                                        </div>
                                        <div class="tooltip-row">
                                            <span class="tooltip-label">PO Number:</span>
                                            <span class="tooltip-value">${orderLineItem?.poNumber ?? ''}</span>
                                        </div>
                                        <div class="tooltip-row">
                                            <span class="tooltip-label">Job Number:</span>
                                            <span class="tooltip-value">${orderLineItem?.customerJobNumber ?? ''}</span>
                                        </div>
                                        <div class="tooltip-row">
                                            <div class="tooltip-row">
                                                <span class="tooltip-label">Order #:</span>
                                                <span class="tooltip-value">${orderLineItem?.orderNumber ?? ''}</span>
                                            </div>
                                            <div class="tooltip-row">
                                                <span class="tooltip-label">Line Item #:</span>
                                                <span class="tooltip-value">${orderLineItem?.orderLineItemNumber}</span>
                                            </div>
                                        </div>
                                        <div class="tooltip-row">
                                            <span class="tooltip-label">Order Created:</span>
                                            <span class="tooltip-value">${formatDate(orderLineItem?.orderCreatedOn, 'Pp')}</span>
                                        </div>
                                        <div class="divider"></div>
                                        <div class="tooltip-row">
                                            <span class="tooltip-label">Description:</span>
                                            <span class="tooltip-value">${orderLineItem?.name ?? ''}</span>
                                        </div>
                                        <div class="tooltip-row">
                                            <span class="tooltip-label">Requirements:</span>
                                            <span class="tooltip-value">${orderLineItem?.requirements ?? ''}</span>
                                        </div>
                                        <div class="tooltip-row">
                                            <span class="tooltip-label">Equipment Type:</span>
                                            <span class="tooltip-value">${orderLineItem?.equipmentTypeType ?? ''}</span>
                                        </div>
                                        <div class="divider"></div>
                                        <div class="tooltip-row">
                                            <div class="tooltip-row">
                                                <span class="tooltip-label">Yard Time:</span>
                                                <span class="tooltip-value">${orderLineItem?.yardTime ? formatDate(orderLineItem?.yardTime, 'h:mm:ss aa') : 'ASAP '}</span>
                                            </div>
                                            <div class="tooltip-row">
                                                <span class="tooltip-label">Onsite Time:</span>
                                                <span class="tooltip-value">${orderLineItem?.onsiteTime ? formatDate(orderLineItem?.onsiteTime, 'h:mm:ss aa') : 'ASAP '}</span>
                                            </div>
                                        </div>
                                        <div class="tooltip-row">
                                            <span class="tooltip-label">Interval:</span>
                                            <span class="tooltip-value">${intervalTime}</span>
                                        </div>
                                        <div class="divider"></div>
                                        ${hideSiteName ? '' : `<div class="tooltip-row">
                                            <span class="tooltip-label">Site:</span>
                                            <span class="tooltip-value">${orderLineItem?.siteName ?? ''}</span>
                                        </div>`}
                                        <div class="tooltip-row">
                                            <span class="tooltip-label">Haul To:</span>
                                            <span class="tooltip-value">${getHaulToAddressForOrderLineItem(orderLineItem)}</span>
                                        </div>
                                        <div class="tooltip-row">
                                            <span class="tooltip-label">Haul From:</span>
                                            <span class="tooltip-value">${getHaulFromAddressForOrderLineItem(orderLineItem)}</span>
                                        </div>
                                        ${getContactInfo(orderLineItem)}
                                        ${getOrderedByInfo(orderLineItem)}
                                    </div>
                                </div>
                            </div>
                            <div class="divider-vertical"></div>
                            <div class="flex-box">
                                ${getMemos(orderLineItem)}
                            </div>
                        </div>`;

    return toolTipHtml;
};
